import { SimNPC } from '../types/SimNPC';
import { Relationship, createRelationship, determineRelationType } from '../types/Relationship';

/**
 * 関係性を更新（双方向）
 *
 * @param npc1 NPC1
 * @param npc2 NPC2
 * @param affectionChange 好感度変化量
 * @param day 現在の日数
 */
export function updateRelationship(
  npc1: SimNPC,
  npc2: SimNPC,
  affectionChange: number,
  day: number
): void {
  // npc1 → npc2
  updateOneWayRelationship(npc1, npc2.id, affectionChange, day);

  // npc2 → npc1（相互）
  updateOneWayRelationship(npc2, npc1.id, affectionChange, day);
}

/**
 * 片方向の関係性を更新
 */
function updateOneWayRelationship(
  npc: SimNPC,
  targetId: string,
  affectionChange: number,
  day: number
): void {
  let rel = npc.relationships.get(targetId);

  if (!rel) {
    // 初めての交流：新規作成
    rel = createRelationship(targetId, day);
    npc.relationships.set(targetId, rel);
  }

  // 好感度更新（-100 ~ 100の範囲）
  rel.affection = Math.max(-100, Math.min(100, rel.affection + affectionChange));
  rel.interactionCount++;
  rel.lastInteractionDay = day;
  rel.relationType = determineRelationType(rel.affection);
}

/**
 * パートナー候補判定（基本条件のみ）
 * マッチスコア計算はFamilyManagerで行う
 *
 * @param npc1 NPC1
 * @param npc2 NPC2
 * @param minAffection 最低好感度（デフォルト40）
 * @returns パートナーになれるか
 */
export function canBecomePartner(
  npc1: SimNPC,
  npc2: SimNPC,
  minAffection: number = 40
): boolean {
  const rel1 = npc1.relationships.get(npc2.id);
  const rel2 = npc2.relationships.get(npc1.id);

  // 双方が最低好感度以上
  return !!(
    rel1 &&
    rel1.affection >= minAffection &&
    rel2 &&
    rel2.affection >= minAffection &&
    npc1.isAlive &&
    npc2.isAlive
  );
}

/**
 * 相互の好感度を取得
 */
export function getMutualAffection(npc1: SimNPC, npc2: SimNPC): { aff1to2: number; aff2to1: number } {
  const rel1 = npc1.relationships.get(npc2.id);
  const rel2 = npc2.relationships.get(npc1.id);
  return {
    aff1to2: rel1?.affection ?? 0,
    aff2to1: rel2?.affection ?? 0,
  };
}

/**
 * 双方の好感度が指定値以上かどうかを判定
 *
 * @param npc1 NPC1
 * @param npc2 NPC2
 * @param minAffection 最低好感度
 * @returns 双方の好感度が指定値以上ならtrue
 */
export function areGoodRelationship(
  npc1: SimNPC,
  npc2: SimNPC,
  minAffection: number
): boolean {
  const { aff1to2, aff2to1 } = getMutualAffection(npc1, npc2);
  return aff1to2 >= minAffection && aff2to1 >= minAffection;
}

/**
 * 関係性を取得
 *
 * @param npc NPC
 * @param targetId 相手のID
 * @returns 関係性（存在しない場合null）
 */
export function getRelationship(npc: SimNPC, targetId: string): Relationship | null {
  return npc.relationships.get(targetId) || null;
}

/**
 * 好感度が高い順にNPCをソート
 *
 * @param npc 基準NPC
 * @param npcs 全NPC
 * @returns 好感度順のNPC配列
 */
export function getSortedByAffection(npc: SimNPC, npcs: SimNPC[]): SimNPC[] {
  return npcs
    .filter((n) => n.id !== npc.id && n.isAlive)
    .sort((a, b) => {
      const affA = npc.relationships.get(a.id)?.affection || 0;
      const affB = npc.relationships.get(b.id)?.affection || 0;
      return affB - affA; // 降順
    });
}
